import { BaseNodeCommand, NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import type { IScenarioRecordable, CommandDeltaContribution } from '../data-profiles/scenarios/ScenarioRecordable';
import { createNodeId } from '../data-profiles/scenarios/ScenarioDelta';
import type { Node } from '../models/scene';
import { getTableDropHitAtPoint } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { InvalidShapeGeometryError } from '../../lib/diagram/shapeGeometry';
import { isPresentationAnnotation, isProjectionOwnedNode } from '../canvas/composeProjectedScene';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

const getNodeSize = (node: Node): { w: number; h: number } => ({
  w: Number(node.w ?? node.width ?? 60) || 60,
  h: Number(node.h ?? node.height ?? 60) || 60,
});

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const resolveMoveForTableDock = (
  node: Node,
  newPosition: { x: number; y: number },
  allNodes: Node[],
): { x: number; y: number; tableDock?: TableDock } | null => {
  const dock = node.attrs?.tableDock as TableDock | undefined;
  if (!dock?.tableId || node.type === 'table' || isProjectionOwnedNode(node)) return null;

  const size = getNodeSize(node);
  const center = {
    x: newPosition.x + size.w / 2,
    y: newPosition.y + size.h / 2,
  };
  const tableNodes = allNodes.filter((candidate) => candidate.type === 'table');
  const hit = tableNodes.map((table) => getTableDropHitAtPoint(table, center)).find(Boolean) ?? null;

  if (!hit) {
    return { x: newPosition.x, y: newPosition.y, tableDock: undefined };
  }

  const contentRect = hit.contentRect;
  const offset = {
    x: clamp(newPosition.x - contentRect.x, 0, Math.max(0, contentRect.w - size.w)),
    y: clamp(newPosition.y - contentRect.y, 0, Math.max(0, contentRect.h - size.h)),
  };

  return {
    x: contentRect.x + offset.x,
    y: contentRect.y + offset.y,
    tableDock: {
      ...dock,
      tableId: hit.tableId,
      cellKey: hit.cellKey,
      offset,
    },
  };
};

function assertFinitePosition(nodeId: string, position: { x: number; y: number }): void {
  const invalidKeys = [
    ...(Number.isFinite(position.x) ? [] : ['x'] as const),
    ...(Number.isFinite(position.y) ? [] : ['y'] as const),
  ];
  if (invalidKeys.length > 0) throw new InvalidShapeGeometryError(nodeId, invalidKeys);
}

function assertFiniteScale(
  nodeId: string,
  size: { w: number; h: number },
  position: { x: number; y: number },
): void {
  const invalidKeys = [
    ...(Number.isFinite(position.x) ? [] : ['x'] as const),
    ...(Number.isFinite(position.y) ? [] : ['y'] as const),
    ...(Number.isFinite(size.w) && size.w > 0 ? [] : ['w'] as const),
    ...(Number.isFinite(size.h) && size.h > 0 ? [] : ['h'] as const),
  ];
  if (invalidKeys.length > 0) throw new InvalidShapeGeometryError(nodeId, invalidKeys);
}

// Command for moving nodes
export class MoveNodeCommand extends BaseNodeCommand implements IScenarioRecordable {
  readonly executionScope?: 'presentation';
  readonly requiresWriteAccess = true;
  private nodeId: string;
  private oldPosition: { x: number; y: number } | null;
  private newPosition: { x: number; y: number };
  private dockedStartPositionsById?: Record<string, { x: number; y: number }>;
  readonly isScenarioRecordable = true as const;
  
  constructor(
    nodeId: string,
    newPosition: { x: number; y: number },
    oldPosition?: { x: number; y: number },
    dockedStartPositionsById?: Record<string, { x: number; y: number }>,
  ) {
    super('Move Node', `Move node to (${Math.round(newPosition.x)}, ${Math.round(newPosition.y)})`);
    assertFinitePosition(nodeId, newPosition);
    this.nodeId = nodeId;
    this.newPosition = newPosition;
    this.oldPosition = oldPosition || null;
    this.dockedStartPositionsById = dockedStartPositionsById;
    const node = useDiagramStore.getState().nodes.find((candidate) => candidate.id === nodeId);
    if (node && isProjectionOwnedNode(node)) this.executionScope = 'presentation';
  }
  
  protected doExecute(): NodeChangeRecord[] {
    const mm: any = this.getMutationManager();
    const store = useDiagramStore.getState();
    const current = store.nodes.find((n) => n.id === this.nodeId) as any;
    if (!current) return [];

    const oldPos = this.oldPosition || { x: current.x ?? 0, y: current.y ?? 0 };
    const deltaX = this.newPosition.x - oldPos.x;
    const deltaY = this.newPosition.y - oldPos.y;

    const dockMove = resolveMoveForTableDock(current, this.newPosition, store.nodes);
    const primaryChanges = dockMove
      ? {
          x: dockMove.x,
          y: dockMove.y,
          attrs: { tableDock: dockMove.tableDock },
        }
      : { x: this.newPosition.x, y: this.newPosition.y };
    const updates: Array<{ nodeId: string; changes: any }> = [{ nodeId: this.nodeId, changes: primaryChanges }];

    if (current.type === 'table' && (deltaX !== 0 || deltaY !== 0)) {
      const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === this.nodeId);
      for (const dn of docked) {
        const start = this.dockedStartPositionsById?.[dn.id];
        const baseX = typeof start?.x === 'number' && Number.isFinite(start.x) ? start.x : ((dn as any).x ?? 0);
        const baseY = typeof start?.y === 'number' && Number.isFinite(start.y) ? start.y : ((dn as any).y ?? 0);
        const nx = baseX + deltaX;
        const ny = baseY + deltaY;
        updates.push({ nodeId: dn.id, changes: { x: nx, y: ny } });
      }
    }

    const records: NodeChangeRecord[] =
      updates.length > 1 && typeof mm.applyBatchNodeChanges === 'function'
        ? mm.applyBatchNodeChanges(updates)
        : (() => {
            const rec = mm.applyNodeChange(this.nodeId, updates[0].changes);
            return rec ? [rec] : [];
          })();

    if (records.length > 0 && this.oldPosition && this.changeRecords.length === 0) {
      const primary = records.find((r) => r.nodeId === this.nodeId);
      if (primary) {
        primary.oldState = { ...primary.oldState, x: this.oldPosition.x, y: this.oldPosition.y };
      }
    }

    return records;
  }
  
  canMergeWith(other: any): boolean {
    if (!(other instanceof MoveNodeCommand)) return false;
    
    // Can merge if it's the same node being moved within a short time
    const timeDiff = other.timestamp - this.timestamp;
    return this.nodeId === other.nodeId && timeDiff < 500; // 500ms window
  }
  
  mergeWith(other: any): MoveNodeCommand {
    // Keep the original position from first command, use new position from other
    return new MoveNodeCommand(
      this.nodeId,
      other.newPosition,
      this.oldPosition || other.oldPosition,
      this.dockedStartPositionsById
    );
  }

  toScenarioDelta(): CommandDeltaContribution {
    const node = useDiagramStore.getState().nodes.find((n) => n.id === this.nodeId);
    const oldX = this.oldPosition?.x ?? node?.x ?? 0;
    const oldY = this.oldPosition?.y ?? node?.y ?? 0;

    return {
      updates: [
        {
          nodeId: createNodeId(this.nodeId),
          change: { field: 'x', oldValue: oldX, newValue: this.newPosition.x },
        },
        {
          nodeId: createNodeId(this.nodeId),
          change: { field: 'y', oldValue: oldY, newValue: this.newPosition.y },
        },
      ],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const node = useDiagramStore.getState().nodes.find((n) => n.id === this.nodeId);
    const oldX = this.oldPosition?.x ?? node?.x ?? 0;
    const oldY = this.oldPosition?.y ?? node?.y ?? 0;

    return {
      updates: [
        {
          nodeId: createNodeId(this.nodeId),
          change: { field: 'x', oldValue: this.newPosition.x, newValue: oldX },
        },
        {
          nodeId: createNodeId(this.nodeId),
          change: { field: 'y', oldValue: this.newPosition.y, newValue: oldY },
        },
      ],
    };
  }
}

// Command for moving multiple nodes
export class MoveNodesCommand extends BaseNodeCommand implements IScenarioRecordable {
  readonly executionScope?: 'presentation';
  readonly requiresWriteAccess = true;
  private moves: Array<{
    nodeId: string;
    oldPosition?: { x: number; y: number };
    newPosition: { x: number; y: number };
  }>;
  private dockedStartPositionsById?: Record<string, { x: number; y: number }>;
  readonly isScenarioRecordable = true as const;
  
  constructor(
    moves: Array<{
      nodeId: string;
      oldPosition?: { x: number; y: number };
      newPosition: { x: number; y: number };
    }>,
    dockedStartPositionsById?: Record<string, { x: number; y: number }>,
  ) {
    super('Move Nodes', `Move ${moves.length} nodes`);
    moves.forEach((move) => assertFinitePosition(move.nodeId, move.newPosition));
    this.moves = moves;
    this.dockedStartPositionsById = dockedStartPositionsById;
    const nodesById = new Map(useDiagramStore.getState().nodes.map((node) => [node.id, node]));
    if (moves.some((move) => {
      const node = nodesById.get(move.nodeId);
      return node && isProjectionOwnedNode(node);
    }) && moves.every((move) => {
      const node = nodesById.get(move.nodeId);
      return node && (isProjectionOwnedNode(node) || isPresentationAnnotation(node));
    })) this.executionScope = 'presentation';
  }
  
  protected doExecute(): NodeChangeRecord[] {
    const store = useDiagramStore.getState();

    // Prepare batch updates
    const nodesById = new Map(store.nodes.map((node) => [node.id, node]));
    const batchUpdates = this.moves.map(move => {
      const node = nodesById.get(move.nodeId);
      const dockMove = node ? resolveMoveForTableDock(node, move.newPosition, store.nodes) : null;
      return {
        nodeId: move.nodeId,
        changes: dockMove
          ? {
              x: dockMove.x,
              y: dockMove.y,
              attrs: { tableDock: dockMove.tableDock },
            }
          : {
              x: move.newPosition.x,
              y: move.newPosition.y
            }
      };
    });

    // If any table is moved, also move its docked nodes by the same delta.
    const movedTableIds = new Set(
      this.moves
        .map((m) => store.nodes.find((n) => n.id === m.nodeId))
        .filter((n: any) => n?.type === 'table')
        .map((n: any) => n.id as string),
    );

    if (movedTableIds.size > 0) {
      for (const m of this.moves) {
        if (!m.oldPosition) continue;
        if (!movedTableIds.has(m.nodeId)) continue;
        const deltaX = m.newPosition.x - m.oldPosition.x;
        const deltaY = m.newPosition.y - m.oldPosition.y;
        if (deltaX === 0 && deltaY === 0) continue;

        const docked = store.nodes.filter((n) => (n as any)?.attrs?.tableDock?.tableId === m.nodeId);
        for (const dn of docked) {
          const start = this.dockedStartPositionsById?.[dn.id];
          const baseX = typeof start?.x === 'number' && Number.isFinite(start.x) ? start.x : ((dn as any).x ?? 0);
          const baseY = typeof start?.y === 'number' && Number.isFinite(start.y) ? start.y : ((dn as any).y ?? 0);
          batchUpdates.push({ nodeId: dn.id, changes: { x: baseX + deltaX, y: baseY + deltaY } });
        }
      }
    }
    
    // Apply all updates in a single batch transaction
    if (batchUpdates.length > 1) {
      const batchRecords = (this.getMutationManager() as any).applyBatchNodeChanges(batchUpdates);
      
      // If we have explicit old positions from drag start, update the records
      if (this.changeRecords.length === 0) {
        const movesById = new Map(this.moves.map((move) => [move.nodeId, move]));
        batchRecords.forEach((record: NodeChangeRecord) => {
          const move = movesById.get(record.nodeId);
          if (move?.oldPosition) {
            record.oldState = { ...record.oldState, x: move.oldPosition.x, y: move.oldPosition.y };
          }
        });
      }
      
      return batchRecords || [];
    } else if (batchUpdates.length === 1) {
      const move = this.moves[0];
      const record = this.getMutationManager().applyNodeChange(move.nodeId, batchUpdates[0].changes);
      
      // If we have explicit old position from drag start, use it
      if (record && move.oldPosition && this.changeRecords.length === 0) {
        record.oldState = { ...record.oldState, x: move.oldPosition.x, y: move.oldPosition.y };
      }
      
      return record ? [record] : [];
    }
    
    return [];
  }

  toScenarioDelta(): CommandDeltaContribution {
    const nodesById = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));

    return {
      updates: this.moves.flatMap((move) => {
        const node = nodesById.get(move.nodeId);
        const oldX = move.oldPosition?.x ?? node?.x ?? 0;
        const oldY = move.oldPosition?.y ?? node?.y ?? 0;
        return [
          {
            nodeId: createNodeId(move.nodeId),
            change: { field: 'x', oldValue: oldX, newValue: move.newPosition.x },
          },
          {
            nodeId: createNodeId(move.nodeId),
            change: { field: 'y', oldValue: oldY, newValue: move.newPosition.y },
          },
        ];
      }),
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const nodesById = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));

    return {
      updates: this.moves.flatMap((move) => {
        const node = nodesById.get(move.nodeId);
        const oldX = move.oldPosition?.x ?? node?.x ?? 0;
        const oldY = move.oldPosition?.y ?? node?.y ?? 0;
        return [
          {
            nodeId: createNodeId(move.nodeId),
            change: { field: 'x', oldValue: move.newPosition.x, newValue: oldX },
          },
          {
            nodeId: createNodeId(move.nodeId),
            change: { field: 'y', oldValue: move.newPosition.y, newValue: oldY },
          },
        ];
      }),
    };
  }
}

// Command for scaling/resizing nodes
export class ScaleNodeCommand extends BaseNodeCommand {
  private nodeId: string;
  private newSize: { w: number; h: number };
  private newPosition: { x: number; y: number };
  
  constructor(
    nodeId: string,
    newSize: { w: number; h: number },
    newPosition: { x: number; y: number }
  ) {
    super('Scale Node', `Resize node to ${Math.round(newSize.w)}x${Math.round(newSize.h)}`);
    assertFiniteScale(nodeId, newSize, newPosition);
    this.nodeId = nodeId;
    this.newSize = newSize;
    this.newPosition = newPosition;
  }
  
  protected doExecute(): NodeChangeRecord[] {
    // Update store first for immediate UI feedback
    // Store → Yjs sync will handle persistence
    const store = useDiagramStore.getState();
    const currentNode = store.nodes.find(n => n.id === this.nodeId);
    if (!currentNode) {
      return [];
    }

    const changes = { 
      x: this.newPosition.x,
      y: this.newPosition.y,
      // Support both legacy (w/h) and generic (width/height) node sizing fields
      w: this.newSize.w,
      h: this.newSize.h,
      width: this.newSize.w as unknown as number,
      height: this.newSize.h as unknown as number,
      // Marker used by shape switching: preserve size across switches only when user has resized at least once
      attrs: { ...((currentNode as any).attrs || {}), userResized: true },
    } as any;

    // Capture old state for undo/redo
    const oldState = {
      x: currentNode.x,
      y: currentNode.y,
      w: currentNode.w,
      h: currentNode.h,
      width: currentNode.width,
      height: currentNode.height,
    };

    // Update store (marks as dirty, triggers store → Yjs sync)
    store.updateNode(this.nodeId, changes);

    // Return change record for undo/redo
    return [{
      nodeId: this.nodeId,
      oldState,
      newState: changes
    }];
  }
  
  canMergeWith(other: any): boolean {
    if (!(other instanceof ScaleNodeCommand)) return false;
    
    // Can merge if it's the same node being scaled within a short time
    const timeDiff = other.timestamp - this.timestamp;
    return this.nodeId === other.nodeId && timeDiff < 500; // 500ms window
  }
  
  mergeWith(other: any): ScaleNodeCommand {
    // Use new size and position from other command
    return new ScaleNodeCommand(
      this.nodeId,
      other.newSize,
      other.newPosition
    );
  }
}

// Command for rotating a node
export class RotateNodeCommand extends BaseNodeCommand {
  private nodeId: string;
  private newRotation: number;
  private oldRotation: number | undefined;

  constructor(nodeId: string, newRotation: number, oldRotation?: number) {
    super('Rotate Node', `Rotate node to ${Math.round(((newRotation % 360) + 360) % 360)}°`);
    this.nodeId = nodeId;
    this.newRotation = newRotation;
    this.oldRotation = oldRotation;
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];
    const record = this.getMutationManager().applyNodeChange(this.nodeId, {
      // Persist rotation under attrs and also mirror to top level for projection sync
      attrs: { rotation: ((this.newRotation % 360) + 360) % 360 },
      rotation: ((this.newRotation % 360) + 360) % 360,
    } as any);
    if (record) {
      // Ensure old rotation is preserved when provided
      if (typeof this.oldRotation === 'number') {
        record.oldState = {
          ...record.oldState,
          attrs: { ...(record.oldState as any).attrs, rotation: this.oldRotation }
        } as any;
      }
      records.push(record);
    }
    return records;
  }
}

// Command for rotating multiple nodes
export class RotateNodesCommand extends BaseNodeCommand {
  private rotations: Array<{ nodeId: string; newRotation: number; oldRotation?: number }>

  constructor(rotations: Array<{ nodeId: string; newRotation: number; oldRotation?: number }>) {
    super('Rotate Nodes', `Rotate ${rotations.length} nodes`);
    this.rotations = rotations;
  }

  protected doExecute(): NodeChangeRecord[] {
    // Prepare batch updates
    const batchUpdates = this.rotations.map(r => ({
      nodeId: r.nodeId,
      changes: {
        attrs: { rotation: ((r.newRotation % 360) + 360) % 360 },
        rotation: ((r.newRotation % 360) + 360) % 360,
      } as any
    }));
    
    // Apply all updates in a single batch transaction
    if (batchUpdates.length > 1) {
      const batchRecords = (this.getMutationManager() as any).applyBatchNodeChanges(batchUpdates);
      
      // Update oldState with explicit old rotations if provided
      batchRecords.forEach((record: NodeChangeRecord, index: number) => {
        const r = this.rotations[index];
        if (typeof r.oldRotation === 'number') {
          record.oldState = {
            ...record.oldState,
            attrs: { ...(record.oldState as any).attrs, rotation: r.oldRotation }
          } as any;
        }
      });
      
      return batchRecords || [];
    } else if (batchUpdates.length === 1) {
      const r = this.rotations[0];
      const rec = this.getMutationManager().applyNodeChange(r.nodeId, batchUpdates[0].changes);
      if (rec) {
        if (typeof r.oldRotation === 'number') {
          rec.oldState = {
            ...rec.oldState,
            attrs: { ...(rec.oldState as any).attrs, rotation: r.oldRotation }
          } as any;
        }
        return [rec];
      }
      return [];
    }
    
    return [];
  }
}

// Command for scaling multiple nodes
export class ScaleNodesCommand extends BaseNodeCommand {
  private scales: Array<{
    nodeId: string;
    newSize: { w: number; h: number };
    newPosition: { x: number; y: number };
  }>;
  
  constructor(
    scales: Array<{
      nodeId: string;
      newSize: { w: number; h: number };
      newPosition: { x: number; y: number };
    }>
  ) {
    super('Scale Nodes', `Resize ${scales.length} nodes`);
    scales.forEach((scale) => assertFiniteScale(scale.nodeId, scale.newSize, scale.newPosition));
    this.scales = scales;
  }
  
  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];
    const batchUpdates: Array<{ id: string; updates: any }> = [];
    const store = useDiagramStore.getState();
    
    this.scales.forEach(scale => {
      const currentNode = store.nodes.find(n => n.id === scale.nodeId);
      if (!currentNode) return;

      const changes = { 
        x: scale.newPosition.x,
        y: scale.newPosition.y,
        w: scale.newSize.w,
        h: scale.newSize.h,
        width: scale.newSize.w as unknown as number,
        height: scale.newSize.h as unknown as number,
        // Marker used by shape switching: preserve size across switches only when user has resized at least once
        attrs: { ...((currentNode as any).attrs || {}), userResized: true },
      } as any;

      // Capture old state for undo/redo
      const oldState = {
        x: currentNode.x,
        y: currentNode.y,
        w: currentNode.w,
        h: currentNode.h,
        width: currentNode.width,
        height: currentNode.height,
      };

      records.push({
        nodeId: scale.nodeId,
        oldState,
        newState: changes
      });

      // Collect updates for batch store update
      batchUpdates.push({ id: scale.nodeId, updates: changes });
    });
    
    // Update store (marks as dirty, triggers store → Yjs sync)
    if (batchUpdates.length > 0) {
      store.updateNodes(batchUpdates);
    }
    
    return records;
  }
}
